import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { IconButton } from "@mui/material";
import clsx from "clsx";
import React, { Ref, useEffect, useRef, useState } from "react";
import { animationsAtom, currentTheme, smetaStore } from "../../../atoms";
import { SmetaSplitterElement } from "../../ComponentInterface";

interface SplitterItemProps {
  orientation?: "vertical" | "horizontal";
  primary: boolean;
  transitionDuration: number;
  oneChild: boolean;
  children: React.ReactNode;
  ref?: Ref<HTMLDivElement>;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const SplitterItem = (props: SplitterItemProps) => {
  const animationsOn = smetaStore.get(animationsAtom);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const styles: React.CSSProperties = { height: "100%" };
  if (props.oneChild) {
    styles.width = "100%";
  } else {
    styles.width = props.orientation === "horizontal" ? "100%" : "";
    styles.display = "flex";
  }
  if (!props.primary) {
    styles.flex = "0 0 auto";
    styles.zIndex = 4;
    if (animationsOn && isMounted) {
      styles.transition =
        props.orientation === "vertical"
          ? `width ${props.transitionDuration}ms`
          : `height ${props.transitionDuration}ms`;
    }
  }
  return (
    <div className="splitter-item" style={styles} ref={props.ref}>
      {props.children}
    </div>
  );
};

interface SplitterLocalProps {
  /** id который будет присвоен элементу где хранятся children и разделитель. В нем же хранятся функции collapse и expand. */
  id?: string;
  style?: React.CSSProperties;
  /** Ориентация сплиттера vertical | horizontal.
   * @default "horizontal"
   */
  orientation?: "vertical" | "horizontal";
  /** Индекс элемента который не коллапсится.
   * @default 0
   */
  primaryIndex?: 0 | 1;
  /** Размер элемента который коллапсится.
   * @default "25%"
   */
  sizeSecondary?: string;
  /** Значение открытый или закрытый сплиттер. Используется при контролируемом компоненте. */
  collapsed?: boolean;
  /** Значение открытый или закрытый сплиттер. Используется при неконтролируемом компоненте. */
  defaultCollapsed?: boolean;
  /** Функция, которая будет вызвана после изменений размеров элемента. */
  onResizeEnd?: (splitterEl: SmetaSplitterElement) => void;
  /** Функция, которая будет вызвана после закрытия сплиттера. При контролируемом компоненте будет вызвана без закрытия. */
  onCollapse?: (splitterEl: SmetaSplitterElement) => void;
  /** Функция, которая будет вызвана после распахивания сплиттера. При контролируемом компоненте будет вызвана без распахивания. */
  onExpand?: (splitterEl: SmetaSplitterElement) => void;
  /** Время анимации в ms.
   * @default 500
   */
  transitionDuration?: number;
  /** Блокировка изменений размеров пользователем. */
  blockResize?: boolean;
  /** Скрытие кнопок collapse и expand. */
  hideButtons?: boolean;
  /** Максимальный размер изменяемого элемента. */
  maxSize?: number;
  /** Минимальный размер изменяемого элемента. */
  minSize?: number;
  /** Скрыть разделитель. */
  hideSplitter?: boolean;
  /** Скрыть разделитель когда сплиттер свернут. */
  hideSplitterCollapsed?: boolean;
  children: React.ReactNode;
  /** ref к которому присвоится ссылка на сплиттер. */
  splitterRef?: React.RefObject<SmetaSplitterElement>;
  /** Блокировка изменений размеров при изменении размера окна. */
  preventResize?: boolean;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function SplitterLocal(props: SplitterLocalProps) {
  const theme = smetaStore.get(currentTheme);
  //Ссылка на сплиттер и его элементы
  const splitterRef = useRef<SmetaSplitterElement>(null!);
  //Ссылка на разделитель
  const separatorRef = useRef<HTMLDivElement>(null!);
  //Ссылка на элемент у которого меняются размеры
  const mutableElementRef = useRef<HTMLDivElement>(null!);
  //Ссылка на движущийся разделитель при перетаскивании
  const splitterFeedbackRef = useRef<HTMLDivElement>(null);
  //Ссылка на задний фон при перетаскивании разделителя
  const splitterBackgroundFeedbackRef = useRef<HTMLDivElement>(null);
  //Состояние коллапса
  const collapsedRef = useRef<boolean>(undefined);
  //Время анимации ресайза и коллапса
  const transitionDuration = props.transitionDuration !== undefined ? props.transitionDuration : 500;
  //Ограничения движущегося разделителя
  const confinesFeedback = [0, 0];
  //Размеры контейнера где находится сплиттер
  const containerSize = useRef({ width: 0, height: 0 });

  //обновление размеров если при рендарах появились новые child
  useEffect(() => {
    const children = React.Children.toArray(props.children);
    if (children.filter((item) => item).length > 1 && splitterRef.current.getAttribute("size") === "100%") {
      const size = props.sizeSecondary ?? "25%";
      splitterRef.current.setAttribute("size", size);
      const currentSize =
        props.orientation === "vertical"
          ? mutableElementRef.current.style.width
          : mutableElementRef.current.style.height;
      if (!currentSize || currentSize === "100%") setSize(size);
    }
  }, [props.children]);

  useEffect(() => {
    if (collapsedRef.current && props.hideSplitterCollapsed) {
      separatorRef.current?.setAttribute("hidden", "");
    } else separatorRef.current?.removeAttribute("hidden");
  }, [props.hideSplitterCollapsed]);

  //первичная установка размеров и функций
  useEffect(() => {
    const splitterParentElement = splitterRef.current.parentElement!;
    splitterRef.current.resize = handleResize;
    containerSize.current = {
      width: splitterParentElement.offsetWidth,
      height: splitterParentElement.offsetHeight,
    };
    let size = "100%";
    if (mutableElementRef.current) {
      splitterRef.current.collapse = collapse;
      splitterRef.current.expand = expand;
      const children = React.Children.toArray(props.children);
      size = children.filter((item) => item).length === 1 ? "100%" : (props.sizeSecondary ?? "25%");
    }
    splitterRef.current.setAttribute("size", size);
  }, []);

  //обновление состояния коллапса
  useEffect(() => {
    const children = React.Children.toArray(props.children);
    if (mutableElementRef.current && children.filter((item) => item).length > 1) {
      if (props.collapsed !== undefined) {
        if (props.collapsed) {
          collapse(false);
        } else expand(false);
      } else {
        if (props.defaultCollapsed) {
          collapse(false);
        } else expand(false);
      }
    }
  }, [props.collapsed]);

  //обновление состояния блокировки ресайза
  useEffect(() => {
    if (mutableElementRef.current && separatorRef.current) {
      if (props.blockResize === true) {
        separatorRef.current.setAttribute("block-resize", "");
      } else separatorRef.current.removeAttribute("block-resize");
    }
  }, [props.blockResize]);

  function handleResize() {
    if (!props.preventResize) {
      const splitterParentElement = splitterRef.current.parentElement!;
      const currentWidth = splitterParentElement.offsetWidth;
      const currentHeight = splitterParentElement.offsetHeight;
      if (containerSize.current.width !== currentWidth || containerSize.current.height !== currentHeight) {
        const currentSize = splitterRef.current?.getAttribute("size") || "";
        if (currentSize.endsWith("px")) {
          if (props.orientation === "vertical") {
            const changeRatio = currentWidth / containerSize.current.width;
            const newWidth = Math.round(parseFloat(currentSize) * changeRatio) + "px";
            splitterRef.current.setAttribute("size", newWidth);
            if (!separatorRef.current.hasAttribute("item-collapsed")) setSize(newWidth);
          } else {
            const changeRatio = currentHeight / containerSize.current.height;
            const newHeight = Math.round(parseFloat(currentSize) * changeRatio) + "px";
            splitterRef.current.setAttribute("size", newHeight);
            if (!separatorRef.current.hasAttribute("item-collapsed")) setSize(newHeight);
          }
        }
        containerSize.current = { width: currentWidth, height: currentHeight };
        if (props.onResizeEnd) {
          if (smetaStore.get(animationsAtom)) {
            setTimeout(() => {
              props.onResizeEnd!(splitterRef.current);
            }, transitionDuration + 1);
          } else {
            props.onResizeEnd(splitterRef.current);
          }
        }
      }
    }
  }

  //функция ограничения передвижения разделителя
  const getPositionFeedback = (current: number) => {
    const minimum = confinesFeedback[0];
    const maximum = confinesFeedback[1];
    if (current <= minimum) {
      splitterFeedbackRef.current.classList.add("limit-reached");
      return minimum;
    }
    if (current >= maximum) {
      splitterFeedbackRef.current.classList.add("limit-reached");
      return maximum;
    }
    splitterFeedbackRef.current.classList.remove("limit-reached");
    return current;
  };

  //функция передвижения разделителя
  const moveSplitterFeedback = (event: MouseEvent | Touch) => {
    const splitterRect = splitterRef.current.getBoundingClientRect();
    if (props.orientation === "vertical") {
      splitterFeedbackRef.current.style.left = getPositionFeedback(event.clientX - splitterRect.x) + "px";
    } else {
      splitterFeedbackRef.current.style.top =
        getPositionFeedback(event.clientY + splitterRef.current.offsetTop - splitterRect.y) + "px";
    }
  };

  //функция передвижения разделителя для тачскрин
  const handleTouchMove = (event: TouchEvent) => {
    moveSplitterFeedback(event.changedTouches[0]);
  };

  //функция установки размеров изменяемого элемента
  function setSize(size: string) {
    if (props.orientation === "vertical") {
      mutableElementRef.current.style.width = size;
    } else mutableElementRef.current.style.height = size;
  }

  //функция конца передвижения разделителя
  const endResize = () => {
    let currentSize;
    if (props.primaryIndex === 1) {
      currentSize =
        props.orientation === "vertical"
          ? splitterFeedbackRef.current.offsetLeft - splitterRef.current.offsetLeft
          : splitterFeedbackRef.current.offsetTop - splitterRef.current.offsetTop;
    } else {
      currentSize =
        props.orientation === "vertical"
          ? splitterRef.current.offsetWidth -
            splitterFeedbackRef.current.offsetLeft -
            separatorRef.current.offsetWidth +
            splitterRef.current.offsetLeft
          : splitterRef.current.offsetHeight -
            splitterFeedbackRef.current.offsetTop -
            separatorRef.current.offsetHeight +
            splitterRef.current.offsetTop;
    }
    currentSize = Math.max(currentSize, 0);
    if (
      !props.minSize &&
      !props.maxSize &&
      currentSize > 0 &&
      splitterFeedbackRef.current.classList.contains("limit-reached")
    ) {
      setSize("calc(100% - 10px)");
      splitterRef.current.setAttribute("size", "calc(100% - 10px)");
    } else {
      setSize(currentSize + "px");
      splitterRef.current.setAttribute("size", currentSize + "px");
    }
    splitterRef.current.toggleAttribute("dragged");
    splitterBackgroundFeedbackRef.current.remove();
    splitterFeedbackRef.current.remove();
    document.removeEventListener("mousemove", moveSplitterFeedback);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("mouseup", endResize);
    document.removeEventListener("touchend", endResize);
    splitterFeedbackRef.current = null;
    if (props.onResizeEnd) {
      if (smetaStore.get(animationsAtom)) {
        setTimeout(() => {
          props.onResizeEnd!(splitterRef.current);
        }, transitionDuration + 1);
      } else {
        props.onResizeEnd(splitterRef.current);
      }
    }
  };

  function collapse(needToDo: boolean) {
    separatorRef.current.setAttribute("item-collapsed", "");
    setSize("0px");
    collapsedRef.current = true;
    if (props.hideSplitterCollapsed) separatorRef.current.setAttribute("hidden", "");
    if (props.onCollapse && needToDo) {
      if (smetaStore.get(animationsAtom)) {
        setTimeout(() => {
          props.onCollapse!(splitterRef.current);
        }, transitionDuration + 1);
      } else props.onCollapse(splitterRef.current);
    }
  }

  function expand(needToDo: boolean) {
    separatorRef.current.removeAttribute("item-collapsed");
    separatorRef.current.removeAttribute("hidden");
    setSize(splitterRef.current.getAttribute("size")!);
    collapsedRef.current = false;
    if (props.onExpand && needToDo) {
      if (smetaStore.get(animationsAtom)) {
        setTimeout(() => {
          props.onExpand!(splitterRef.current);
        }, transitionDuration + 1);
      } else props.onExpand(splitterRef.current);
    }
  }

  //функция начала передвижения разделителя и нажатии кнопки коллапса
  const handleSplitterMouseDown = (event: React.MouseEvent | React.TouchEvent) => {
    if (event.type === "mousedown" && (event as React.MouseEvent).button !== 0) return;
    const target = event.target as HTMLDivElement;
    if (target.classList.contains("separator-splitter")) {
      //если нажали на разделитель
      if (!separatorRef.current.hasAttribute("item-collapsed")) {
        let minSize, maxSize;
        const separatorPreviousElement = separatorRef.current.previousElementSibling! as HTMLDivElement,
          separatorNextElement = separatorRef.current.nextElementSibling! as HTMLDivElement;
        if (props.orientation === "vertical") {
          minSize = separatorPreviousElement.offsetLeft;
          maxSize =
            separatorNextElement.offsetLeft + separatorNextElement.offsetWidth - separatorRef.current.offsetWidth;
        } else {
          minSize = separatorPreviousElement.offsetTop;
          maxSize =
            separatorNextElement.offsetTop + separatorNextElement.offsetHeight - separatorRef.current.offsetHeight;
        }
        if (props.minSize) {
          if (minSize < props.minSize)
            if (props.primaryIndex === 1) {
              minSize = props.minSize;
            } else maxSize -= props.minSize;
        }
        if (props.maxSize) {
          if (maxSize > props.maxSize)
            if (props.primaryIndex === 1) {
              maxSize = props.maxSize;
            } else minSize = maxSize - props.maxSize + (props.minSize || 0);
        }
        confinesFeedback[0] = minSize;
        confinesFeedback[1] = maxSize;
        splitterBackgroundFeedbackRef.current = document.createElement("div");
        splitterBackgroundFeedbackRef.current.className = "splitter-background-feedback";
        splitterBackgroundFeedbackRef.current.style.cursor =
          props.orientation === "vertical" ? "ew-resize" : "ns-resize";
        splitterFeedbackRef.current = document.createElement("div");
        splitterFeedbackRef.current.className = "splitter-feedback";
        splitterFeedbackRef.current.style.left = separatorRef.current.offsetLeft + "px";
        splitterFeedbackRef.current.style.top = separatorRef.current.offsetTop + "px";
        splitterFeedbackRef.current.style.width = separatorRef.current.offsetWidth + "px";
        splitterFeedbackRef.current.style.height = separatorRef.current.offsetHeight + "px";
        splitterRef.current.appendChild(splitterFeedbackRef.current);
        document.body.appendChild(splitterBackgroundFeedbackRef.current);
        splitterRef.current.toggleAttribute("dragged");
        document.addEventListener("mousemove", moveSplitterFeedback);
        document.addEventListener("touchmove", handleTouchMove);
        document.addEventListener("mouseup", endResize);
        document.addEventListener("touchend", endResize);
      }
    } else {
      //если нажали на кнопку коллапса
      if (props.collapsed !== undefined) {
        if (props.collapsed) {
          props.onExpand?.(splitterRef.current);
        } else props.onCollapse?.(splitterRef.current);
      } else {
        if (collapsedRef.current) {
          expand(true);
        } else collapse(true);
      }
    }
    event.preventDefault();
  };

  //создание дочерних элементов сплиттера
  const splitterElements = [];
  const children = React.Children.toArray(props.children);
  const primaryElement = props.primaryIndex === 1 ? 1 : 0;
  for (let i = 0; i < children.length; i++) {
    splitterElements.push(
      <SplitterItem
        orientation={props.orientation}
        primary={i === primaryElement}
        ref={i === primaryElement ? null : mutableElementRef}
        transitionDuration={transitionDuration}
        oneChild={children.length <= 1}
      >
        {children[i]}
      </SplitterItem>,
    );
  }

  return (
    <div
      className={clsx("splitter", props.orientation === "vertical" ? "vertical-splitter" : "horizontal-splitter")}
      ref={(el) => {
        splitterRef.current = el;
        if (props.splitterRef) props.splitterRef.current = el;
      }}
      id={props.id}
      style={props.style}
      data-smetaresizer=""
    >
      {splitterElements[0]}
      {splitterElements.length > 1 && (
        <div
          role="separator"
          className={clsx("separator-splitter", { light: theme === "light" })}
          primaryelement={primaryElement}
          ref={separatorRef}
          onMouseDown={handleSplitterMouseDown}
          onTouchStart={handleSplitterMouseDown}
          style={props.hideSplitter ? { height: 0, width: 0, visibility: "hidden", display: "none" } : {}}
        >
          <IconButton
            color="inherit"
            size="small"
            className="collapse-button"
            far-button=""
            style={{ visibility: props.hideButtons ? "hidden" : undefined }}
          >
            <KeyboardArrowDownIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            color="inherit"
            size="small"
            className="collapse-button"
            near-button=""
            style={{ visibility: props.hideButtons ? "hidden" : undefined }}
          >
            <KeyboardArrowDownIcon fontSize="inherit" />
          </IconButton>
        </div>
      )}
      {splitterElements.length > 1 && splitterElements[1]}
    </div>
  );
}
